import { supabase } from '@/integrations/supabase/client';

export type ListingType = 'property' | 'plot' | 'project' | 'commercial';
export type RevealType = 'phone' | 'whatsapp' | 'email';

const SESSION_KEY = 'er_track_session';
const VIEW_DEDUP_PREFIX = 'er_view_';
const REVEAL_DEDUP_PREFIX = 'er_reveal_';
const VIEW_DEDUP_WINDOW_MS = 30 * 60 * 1000;   // 30 min
const REVEAL_DEDUP_WINDOW_MS = 60 * 60 * 1000; // 1 hr

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no_session';
  }
}

function recentlyTracked(key: string, windowMs: number): boolean {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    if (Date.now() - ts > windowMs) return false;
    return true;
  } catch {
    return false;
  }
}

function markTracked(key: string) {
  try {
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    /* noop */
  }
}

interface TrackViewArgs {
  listingType: ListingType;
  listingId: string;
  city?: string | null;
  source?: string | null;
}

export async function trackView({ listingType, listingId, city, source }: TrackViewArgs) {
  if (!listingId) return;
  const dedupKey = `${VIEW_DEDUP_PREFIX}${listingType}_${listingId}`;
  if (recentlyTracked(dedupKey, VIEW_DEDUP_WINDOW_MS)) return;
  markTracked(dedupKey);

  try {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('listing_views' as any).insert({
      listing_type: listingType,
      listing_id: listingId,
      viewer_id: user?.id ?? null,
      session_id: getSessionId(),
      city: city ?? null,
      source: source ?? (typeof document !== 'undefined' ? document.referrer || null : null),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 250) : null,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[tracking] view insert failed', err);
  }
}

interface TrackRevealArgs {
  listingType: ListingType;
  listingId: string;
  revealType: RevealType;
  city?: string | null;
  source?: string | null;
}

export async function trackReveal({ listingType, listingId, revealType, city, source }: TrackRevealArgs) {
  if (!listingId) return;
  const dedupKey = `${REVEAL_DEDUP_PREFIX}${listingType}_${listingId}_${revealType}`;
  if (recentlyTracked(dedupKey, REVEAL_DEDUP_WINDOW_MS)) return;
  markTracked(dedupKey);

  try {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('contact_reveals' as any).insert({
      listing_type: listingType,
      listing_id: listingId,
      viewer_id: user?.id ?? null,
      session_id: getSessionId(),
      city: city ?? null,
      source: source ?? null,
      reveal_type: revealType,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[tracking] reveal insert failed', err);
  }
}
